import { observabilityDb } from './observability-db';

const TTL_MS = 5 * 24 * 60 * 60 * 1000;
/** Re-sweep at most this often during a long-lived session -- a PWA can stay
 * open for days without a fresh load, so boot-only sweeping under-prunes. */
const OPPORTUNISTIC_SWEEP_INTERVAL_MS = 60 * 60 * 1000;
/** Approximate; the store shares origin quota with cached magazine content,
 * so this must stay low enough that observability can never be the reason a
 * reader's downloaded issue gets evicted. */
const BYTE_BUDGET = 5 * 1024 * 1024;

/**
 * Age-based eviction, run at boot and opportunistically (gated by the
 * `lastTtlSweepAt` marker so the delete query doesn't run on every write).
 * Safe to call liberally -- it no-ops when called again inside the
 * opportunistic window.
 */
export async function sweepExpiredEvents(
	now: number = Date.now(),
): Promise<void> {
	const marker = await observabilityDb.meta.get('lastTtlSweepAt');
	if (marker && now - marker.value < OPPORTUNISTIC_SWEEP_INTERVAL_MS) {
		return;
	}

	await observabilityDb.events
		.where('timestamp')
		.below(now - TTL_MS)
		.delete();
	await observabilityDb.meta.put({ key: 'lastTtlSweepAt', value: now });
}

/**
 * Byte-budget eviction. Row count doesn't bound bytes once rows are
 * variable-size (interned documents make `query_error` rows larger than
 * e.g. `db_open_failure`), so this trims oldest-first against an
 * approximate size instead of a row cap.
 */
export async function trimToByteBudget(): Promise<void> {
	let totalBytes = await sumApproxBytes();
	if (totalBytes <= BYTE_BUDGET) {
		return;
	}

	// Oldest-first, one row at a time -- this only runs when already over
	// budget, which should be rare given the TTL sweep and in-memory dedupe
	// upstream of every write, so simplicity wins over batching here.
	const oldestFirst = observabilityDb.events.orderBy('timestamp');
	await oldestFirst.each(async (row) => {
		if (totalBytes <= BYTE_BUDGET) {
			return;
		}
		await observabilityDb.events.delete(row.id as number);
		totalBytes -= row.approxBytes;
	});
}

async function sumApproxBytes(): Promise<number> {
	let total = 0;
	await observabilityDb.events.each((row) => {
		total += row.approxBytes;
	});
	return total;
}
